import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Textarea } from './ui/textarea';
import { Trash2, MousePointerClick, Flag, Plus, Pencil, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '../lib/supabase';
import api from '../lib/api';
import AdminBannerFormModal from './AdminBannerFormModal';

const STATUS_BADGE = {
  active: { labelKey: 'banner.statusActive', className: 'bg-green-50 text-green-700 border-green-200' },
  expired: { labelKey: 'banner.statusExpired', className: 'bg-gray-100 text-gray-600 border-gray-200' },
  taken_down: { labelKey: 'banner.statusTakenDown', className: 'bg-red-50 text-red-700 border-red-200' },
};

/**
 * Admin moderation view for employer homepage banners (spec §10 / plan Task 7).
 * Lists GET /api/admin/banners (already reported-first ordered server-side —
 * see banner_service.get_admin_list) with image preview, click count, a reported
 * flag, and a Takedown action. Takedown calls the single-winner atomic
 * takedown_banner RPC (via POST /api/admin/banners/{id}/takedown), which refunds
 * the employer pro-rata for unused days — the confirm dialog says so up front.
 */
const AdminBannersSection = () => {
  const { t } = useTranslation();
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [takedownTarget, setTakedownTarget] = useState(null);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showPlatformForm, setShowPlatformForm] = useState(false);
  const [platformEditTarget, setPlatformEditTarget] = useState(null);
  const [platformDeleteTarget, setPlatformDeleteTarget] = useState(null);

  const fetchBanners = async () => {
    try {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.access_token) { setLoading(false); return; }
      const res = await api.get('/api/admin/banners', {
        headers: { Authorization: `Bearer ${session.session.access_token}` }
      });
      setBanners(res.data?.banners || []);
    } catch (error) {
      console.error('[AdminBannersSection] Failed to fetch banners:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBanners();
  }, []);

  const openTakedown = (banner) => {
    setTakedownTarget(banner);
    setReason('');
  };

  const confirmPlatformDelete = async () => {
    if (!platformDeleteTarget || submitting) return;
    setSubmitting(true);
    try {
      const { data: session } = await supabase.auth.getSession();
      await api.delete(`/api/admin/banners/${platformDeleteTarget.id}`, {
        headers: { Authorization: `Bearer ${session.session.access_token}` },
      });
      toast.success(t('banner.deleteSuccess', 'Banner deleted.'));
      setPlatformDeleteTarget(null);
      fetchBanners();
    } catch (error) {
      console.error('[AdminBannersSection] Platform delete failed:', error);
      toast.error(t('banner.deleteError', 'Failed to delete the banner. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  const confirmTakedown = async () => {
    if (!takedownTarget || submitting) return;
    setSubmitting(true);
    try {
      const { data: session } = await supabase.auth.getSession();
      const res = await api.post(
        `/api/admin/banners/${takedownTarget.id}/takedown`,
        { reason: reason.trim() || undefined },
        { headers: { Authorization: `Bearer ${session.session.access_token}` } }
      );
      const refunded = res.data?.refunded_credits || 0;
      toast.success(refunded > 0
        ? t('banner.takedownSuccessRefunded', { credits: refunded })
        : t('banner.takedownSuccess'));
      setTakedownTarget(null);
      fetchBanners();
    } catch (error) {
      console.error('[AdminBannersSection] Takedown failed:', error);
      toast.error(t('banner.takedownFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Card data-testid="table-employer-banners">
        <CardHeader>
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <CardTitle>{t('banner.adminTitle')}{!loading ? ` (${banners.length})` : ''}</CardTitle>
            <Button size="sm" onClick={() => setShowPlatformForm(true)} data-testid="create-platform-banner-btn">
              <Plus size={15} className="mr-1" />
              {t('banner.adminCreateTitle', 'New platform banner')}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-sm text-muted-foreground text-center py-8">{t('common.loading', 'Loading...')}</p>
          ) : banners.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">{t('banner.adminEmpty')}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[700px]">
                <thead className="border-b bg-muted/30">
                  <tr>
                    <th className="py-3 px-4 text-left text-sm font-semibold">Image</th>
                    <th className="py-3 px-4 text-left text-sm font-semibold">Status</th>
                    <th className="py-3 px-4 text-left text-sm font-semibold">Clicks</th>
                    <th className="py-3 px-4 text-left text-sm font-semibold">Apps</th>
                    <th className="py-3 px-4 text-left text-sm font-semibold hidden md:table-cell">Created</th>
                    <th className="py-3 px-4 text-left text-sm font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {banners.map((b) => {
                    const badge = STATUS_BADGE[b.status] || STATUS_BADGE.expired;
                    return (
                      <tr key={b.id} className={`hover:bg-muted/50 ${b.reported ? 'bg-red-50/60 dark:bg-red-950/20' : ''}`}>
                        <td className="py-2 px-4">
                          {b.image_url ? (
                            <img src={b.image_url} alt="" className="w-24 h-8 object-cover rounded border" />
                          ) : (
                            <span className="text-xs text-muted-foreground">&mdash;</span>
                          )}
                        </td>
                        <td className="py-2 px-4">
                          <div className="flex items-center gap-2 flex-wrap">
                            <Badge variant="outline" className={badge.className}>{t(badge.labelKey)}</Badge>
                            {b.kind === 'platform' && (
                              <Badge variant="outline" className="text-xs bg-amber-50 text-amber-800 border-amber-300">
                                {t('banner.featured', 'Featured')}
                              </Badge>
                            )}
                            {b.reported && (
                              <Badge variant="destructive" className="text-xs flex items-center gap-1" data-testid="banner-reported-badge">
                                <Flag size={10} />
                                {t('banner.adminReportedBadge')}
                              </Badge>
                            )}
                          </div>
                        </td>
                        <td className="py-2 px-4 text-sm">
                          <span className="flex items-center gap-1 text-muted-foreground">
                            <MousePointerClick size={14} />
                            {b.click_count || 0}
                          </span>
                        </td>
                        <td className="py-2 px-4 text-sm">
                          {/* Applications attributed via the banner click→apply funnel
                              (?ref_banner). Admin-proprietary — not shown to employers. */}
                          <span className="flex items-center gap-1 text-muted-foreground">
                            <FileText size={14} />
                            {b.kind === 'platform' ? '—' : (b.application_count || 0)}
                          </span>
                        </td>
                        <td className="py-2 px-4 text-xs text-muted-foreground hidden md:table-cell whitespace-nowrap">
                          {b.created_at ? new Date(b.created_at).toLocaleDateString() : '-'}
                        </td>
                        <td className="py-2 px-4">
                          {b.status === 'active' ? (
                            b.kind === 'platform' ? (
                              <div className="flex gap-1.5 flex-wrap">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-xs whitespace-nowrap"
                                  onClick={() => setPlatformEditTarget(b)}
                                  data-testid="platform-banner-edit-btn"
                                >
                                  <Pencil size={13} className="mr-1" />
                                  {t('banner.editBanner', 'Edit')}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="destructive"
                                  className="text-xs whitespace-nowrap"
                                  onClick={() => setPlatformDeleteTarget(b)}
                                  data-testid="platform-banner-delete-btn"
                                >
                                  <Trash2 size={13} className="mr-1" />
                                  {t('banner.deleteBanner', 'Delete')}
                                </Button>
                              </div>
                            ) : (
                              <Button
                                size="sm"
                                variant="destructive"
                                className="text-xs whitespace-nowrap"
                                onClick={() => openTakedown(b)}
                                data-testid="banner-takedown-btn"
                              >
                                <Trash2 size={14} className="mr-1" />
                                {t('banner.takedownButton')}
                              </Button>
                            )
                          ) : (
                            <span className="text-xs text-muted-foreground">&mdash;</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Takedown confirm dialog */}
      <Dialog open={!!takedownTarget} onOpenChange={(open) => { if (!open && !submitting) setTakedownTarget(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <Trash2 size={20} />
              {t('banner.takedownConfirmTitle')}
            </DialogTitle>
            <DialogDescription>{t('banner.takedownConfirmBody')}</DialogDescription>
          </DialogHeader>

          <div className="space-y-2 py-2">
            <label className="text-sm font-medium" htmlFor="banner-takedown-reason">
              {t('banner.takedownReasonLabel')}
            </label>
            <Textarea
              id="banner-takedown-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t('banner.takedownReasonPlaceholder')}
              rows={3}
            />
          </div>

          <DialogFooter>
            <Button variant="outline" disabled={submitting} onClick={() => setTakedownTarget(null)}>
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button
              variant="destructive"
              disabled={submitting}
              onClick={confirmTakedown}
              data-testid="confirm-banner-takedown-btn"
            >
              {submitting ? '...' : t('banner.takedownConfirmAction')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Platform banner create / edit (admin-only, URL-linked, no credits) */}
      {showPlatformForm && (
        <AdminBannerFormModal
          banner={null}
          onClose={(didSave) => { setShowPlatformForm(false); if (didSave) fetchBanners(); }}
        />
      )}
      {platformEditTarget && (
        <AdminBannerFormModal
          banner={platformEditTarget}
          onClose={(didSave) => { setPlatformEditTarget(null); if (didSave) fetchBanners(); }}
        />
      )}

      {/* Platform banner delete confirm (no refund involved — none was charged) */}
      <Dialog open={!!platformDeleteTarget} onOpenChange={(open) => { if (!open && !submitting) setPlatformDeleteTarget(null); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <Trash2 size={20} />
              {t('banner.deleteConfirmTitle', 'Delete this banner?')}
            </DialogTitle>
            <DialogDescription>
              {t('banner.adminDeleteConfirmBody', 'This removes the platform banner from the homepage immediately.')}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" disabled={submitting} onClick={() => setPlatformDeleteTarget(null)}>
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button
              variant="destructive"
              disabled={submitting}
              onClick={confirmPlatformDelete}
              data-testid="confirm-platform-banner-delete-btn"
            >
              {submitting ? '...' : t('banner.deleteBanner', 'Delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AdminBannersSection;
